import { HttpErrorResponse } from '@angular/common/http';

interface ServerErrorPayload {
  message?: string;
  success?: boolean;
  errorCode?: string;
  errors?: string;
  customCode?: number;
}

/**
 * Standardized API error — Angular port of Consumer's network/ApiError.ts.
 * `messageKey` holds the i18n key (under the `common.error.*` namespace) so
 * callers can render it with the `translate` pipe; falls back to any
 * server-supplied message.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly isNetworkError: boolean;
  readonly isServerError: boolean;
  readonly isAuthError: boolean;
  readonly serverMessage?: string;
  readonly messageKey: string;
  /**
   * The raw, parsed error response body (`HttpErrorResponse.error`), when there was one — e.g.
   * `{ success: false, vdi_provisioning_status: 'failed', timer_expires_at: null, ... }`. Most
   * callers only need `code`/`serverMessage`, but some endpoints put meaningful, typed data in
   * the body of an otherwise-error HTTP response (BE's confirmed contract for
   * `GET /api/user/provisioning/status`: the `in_progress` and `failed` provisioning states both
   * arrive as HTTP 400, not 200) — `body` lets a caller that knows to expect this recover that
   * data instead of only seeing a generic thrown error. See dashboard-repository.service.ts's
   * `getProvisioningStatus` for the one current consumer.
   */
  readonly body?: unknown;

  constructor(
    messageKey: string,
    status = 0,
    code = 'UNKNOWN_ERROR',
    serverMessage?: string,
    body?: unknown,
  ) {
    super(serverMessage || messageKey);
    this.name = 'ApiError';
    this.messageKey = messageKey;
    this.status = status;
    this.code = code;
    this.serverMessage = serverMessage;
    this.body = body;
    this.isNetworkError = status === 0;
    this.isServerError = status >= 500;
    this.isAuthError = status === 401 || status === 403;
  }

  static fromHttpErrorResponse(error: HttpErrorResponse): ApiError {
    if (error.status === 0) {
      return new ApiError('common.error.noNetwork', 0, 'ERR_NETWORK');
    }

    const data = error.error as ServerErrorPayload | undefined;
    const serverMsg = data?.message;

    switch (error.status) {
      case 401:
        // Preserve the server's specific code when it sends one (e.g. login 2FA's
        // TWO_FACTOR_REQUIRED, distinct from a plain expired-session 401) — falls
        // back to the generic UNAUTHORIZED when the body doesn't carry one.
        return new ApiError('common.error.sessionExpired', 401, data?.errors || 'UNAUTHORIZED', serverMsg, error.error);
      case 403:
        return new ApiError('common.error.forbidden', 403, 'FORBIDDEN', serverMsg, error.error);
      case 404:
        return new ApiError('common.error.notFound', 404, 'NOT_FOUND', serverMsg, error.error);
      case 422:
        return new ApiError('common.error.validationError', 422, 'VALIDATION_ERROR', serverMsg, error.error);
      case 429:
        return new ApiError('common.error.rateLimited', 429, 'RATE_LIMITED', serverMsg, error.error);
      default:
        if (error.status >= 500) {
          return new ApiError('common.error.serverError', error.status, 'SERVER_ERROR', serverMsg, error.error);
        }
        return new ApiError(
          'common.error.generic',
          error.status,
          data?.errors || data?.errorCode || 'API_ERROR',
          serverMsg,
          error.error,
        );
    }
  }
}

/**
 * The CMS-provided `x-api-key` isn't available yet (CMS config not loaded). Thrown by both
 * `authInterceptor` and `TokenRefreshService`, and treated as a transient failure — it says
 * nothing about the user's session.
 */
export class MissingApiKeyError extends Error {
  constructor(context: string) {
    super(`Missing CMS-provided API key — ${context}`);
    this.name = 'MissingApiKeyError';
  }
}
